import { claudeSkillSecurityScannerCopy } from "../claude-skill-security-copy.js";
import { importWithChunkReload } from "./chunk-reload";
import { claudeSkillSecurityScannerIntentSelectors } from "./claude-skill-security-intent";
import { installSingleOpenFaqAccordion } from "./faq-accordion";
import type { ScannerIntentState } from "./scanner-intent-bootstrap";
import { installScannerIntentBootstrap } from "./scanner-intent-bootstrap";

declare global {
  interface Window {
    __SKILLSECURITY_CLAUDE_SKILL_SECURITY_HYDRATED__?: boolean;
    __SKILLSECURITY_CLAUDE_SKILL_SECURITY_INITIAL_STATE__?: ScannerIntentState;
  }
}

const rootSelector = "#content-scanner-root";
const rootElement = document.querySelector<HTMLElement>(rootSelector);
let cleanupScanner = (): void => undefined;

if (rootElement !== null) {
  const setSubmitIntentPending = (pending: boolean): void => {
    rootElement.toggleAttribute("data-scan-intent-pending", pending);
  };
  const scannerBootstrap = installScannerIntentBootstrap({
    selectors: claudeSkillSecurityScannerIntentSelectors,
    readState: () => window.__SKILLSECURITY_CLAUDE_SKILL_SECURITY_INITIAL_STATE__,
    writeState: (state) => {
      window.__SKILLSECURITY_CLAUDE_SKILL_SECURITY_INITIAL_STATE__ = state;
    },
    isHydrated: () => window.__SKILLSECURITY_CLAUDE_SKILL_SECURITY_HYDRATED__ === true,
    markHydrated: () => {
      window.__SKILLSECURITY_CLAUDE_SKILL_SECURITY_HYDRATED__ = true;
    },
    onSubmitIntentPendingChange: setSubmitIntentPending,
    loadMount: async () => {
      const { mountClaudeSkillSecurityScanner } = await importWithChunkReload(
        () => import("./claude-skill-security-scanner"),
      );
      return () => {
        mountClaudeSkillSecurityScanner(rootElement, {
          locale: "en",
          scannerCopy: claudeSkillSecurityScannerCopy,
          initialState: window.__SKILLSECURITY_CLAUDE_SKILL_SECURITY_INITIAL_STATE__,
          initialTextareaValue: rootElement.querySelector("textarea")?.value ?? "",
          onInitialStateConsumed: () => {
            delete window.__SKILLSECURITY_CLAUDE_SKILL_SECURITY_INITIAL_STATE__;
          },
          onStateChange: (state) => {
            if (state.kind !== "idle") {
              setSubmitIntentPending(false);
            }
          },
          fileInputId: "claude-skill-source-file",
        });
      };
    },
  });
  // Begin loading the hydrated product surface immediately so its route-owned Motion variant tree
  // can run on first view. Intent listeners remain active while the chunk is in flight and preserve
  // paste, file, drop, keyboard, and submit state.
  void scannerBootstrap.hydrate();
  cleanupScanner = () => scannerBootstrap.dispose();
}

const cleanupFaq = installSingleOpenFaqAccordion({
  summarySelector: ".claude-security-faq-list summary",
  listSelector: ".claude-security-faq-list",
});
let cleanupMotion = (): void => undefined;
let cleanupReportLens = (): void => undefined;
let cleanupReportObserver = (): void => undefined;
let pageDisposed = false;

async function initializeStaticEnhancements(): Promise<void> {
  const reportRoot = document.querySelector<HTMLElement>("#content-report-root");
  const motionModule = await importWithChunkReload(() => import("./claude-skill-security-motion"));
  if (pageDisposed) {
    return;
  }
  cleanupMotion = motionModule.installClaudeSkillSecurityMotion({ includeReportLens: false });
  if (reportRoot === null) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }
      observer.disconnect();
      void importWithChunkReload(() => import("./claude-skill-security-report")).then(
        async (reportModule) => {
          if (pageDisposed) {
            return;
          }
          reportModule.mountClaudeSkillSecurityReport(reportRoot);
          await nextAnimationFrame();
          if (pageDisposed) {
            return;
          }
          cleanupReportLens = motionModule.installClaudeSkillSecurityReportLens();
        },
      );
    },
    { rootMargin: "600px 0px" },
  );
  cleanupReportObserver = () => observer.disconnect();
  observer.observe(reportRoot);
}

function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

void initializeStaticEnhancements();

function handlePageHide(event: PageTransitionEvent): void {
  if (event.persisted) {
    return;
  }

  pageDisposed = true;
  window.removeEventListener("pagehide", handlePageHide);
  cleanupScanner();
  cleanupReportObserver();
  cleanupMotion();
  cleanupReportLens();
  cleanupFaq();
}

window.addEventListener("pagehide", handlePageHide);

export {};
